import { useState } from 'react'

import type { PasswordLinkShown } from '../api/types'
import { t } from '../i18n/lingua'
import { CopyableId, ErrorBanner, formatDateTime } from './common'
import { Finestra } from './ModificheGitlab'

/**
 * «Link password» for one person (tenant Users and platform Users): mail it to
 * them, or generate it and show it here to hand over by another channel. The
 * shown link is seen once — only its fingerprint is kept — and whoever holds it
 * can choose the person's password, so the window says so; generating it
 * expires the previous links and lands in the access log.
 */
export function LinkPassword({ email, possoMostrare, perchéNo, manda, mostra, onChiudi }: {
  email: string
  possoMostrare: boolean
  perchéNo?: string
  manda: () => Promise<{ sent: 'invite' | 'reset'; email: string }>
  mostra: () => Promise<PasswordLinkShown>
  onChiudi: () => void
}) {
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [inviato, setInviato] = useState<string | null>(null)
  const [link, setLink] = useState<PasswordLinkShown | null>(null)

  async function esegui(fn: () => Promise<void>) {
    setLavoro(true)
    setErrore(null)
    try {
      await fn()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const perMail = () => {
    if (!window.confirm(t('Mandare a {email} un collegamento per scegliere la password? Se ne ha già una, il collegamento vale un’ora e quella attuale resta valida finché non la cambia.', { email }))) return
    void esegui(async () => {
      const r = await manda()
      setLink(null)
      setInviato(r.sent === 'invite'
        ? t('Invito rimandato a {email}.', { email: r.email })
        : t('Collegamento per la nuova password mandato a {email}.', { email: r.email }))
    })
  }
  const mostraLink = () => {
    if (!window.confirm(t('Generare un nuovo link per {email}? Chi lo ha può scegliere la password al suo posto: consegnalo solo a questa persona. I link mandati prima smettono di valere.', { email }))) return
    void esegui(async () => {
      setInviato(null)
      setLink(await mostra())
    })
  }

  return (
    <Finestra titolo={t('Link password — {email}', { email })}
              sotto={t('Un collegamento per scegliere la password: a chi non l’ha mai scelta rimanda l’invito, agli altri un link valido un’ora. Si usa una volta sola, e chi amministra non vede né sceglie la password.')}
              onChiudi={onChiudi}>
      <div className="form-dialog">
        <ErrorBanner error={errore} />
        {inviato && <div className="banner banner--ok">{inviato}</div>}
        {link && (
          <>
            <div className="banner banner--warn">
              {t('Chi ha questo link può scegliere la password di {email}: consegnalo solo a questa persona, per un canale sicuro. Lo vedi solo adesso; i link precedenti non valgono più. La generazione è nel registro degli accessi.', { email: link.email })}
            </div>
            <CopyableId label={link.kind === 'invite' ? t('Invito') : t('Link')} value={link.url} />
            <p className="field__hint">
              {t('Vale fino al {quando}, una volta sola.', { quando: formatDateTime(link.expires_at) })}
            </p>
          </>
        )}
        {!possoMostrare && perchéNo && <p className="field__hint">{perchéNo}</p>}
        <div className="form-dialog__azioni">
          <button type="button" disabled={lavoro} onClick={perMail}>{t('Manda per mail')}</button>
          <button type="button" className="secondary" disabled={lavoro || !possoMostrare} onClick={mostraLink}>
            {t('Genera e mostra il link')}
          </button>
        </div>
      </div>
    </Finestra>
  )
}
